import React from "react";
import { Flex, Text, Avatar, Box } from "@chakra-ui/react";
import { CheckCircleIcon, CloseIcon } from "@chakra-ui/icons";
import {
  getAttendeeStatus,
  getAttendeeResponseDetails,
  ATTENDEE_STATUS,
  formatTimestamp,
} from "@utils/attendeeHelpers";
import { activityTypes } from "@constants/activities";

const AttendeeCard = ({ attendee, activityType, options = [], activity }) => {
  const status = getAttendeeStatus(attendee, activityType, options);
  const responseDetails = getAttendeeResponseDetails(
    attendee,
    activityType,
    options,
    activity,
  );

  // Check if Share activity has links
  const hasLinks =
    activityType === activityTypes.share &&
    activity &&
    activity.link &&
    activity.link.trim() !== "";

  // Status indicator component
  const StatusIndicator = () => {
    switch (status) {
      case ATTENDEE_STATUS.ANSWERED_CORRECT:
        return (
          <CheckCircleIcon
            w={4}
            h={4}
            color="green.500"
            bg="white"
            borderRadius="full"
            data-testid="attendee-status-correct"
          />
        );

      case ATTENDEE_STATUS.VIEWED:
        // Yellow for Send with links, green for Send without links or other activities
        return (
          <CheckCircleIcon
            w={4}
            h={4}
            color={hasLinks ? "yellow.500" : "green.500"}
            bg="white"
            borderRadius="full"
            data-testid={
              hasLinks ? "attendee-status-viewed" : "attendee-status-correct"
            }
          />
        );

      case ATTENDEE_STATUS.OPENED:
        return (
          <CheckCircleIcon
            w={4}
            h={4}
            color="green.500"
            bg="white"
            borderRadius="full"
            data-testid="attendee-status-correct"
          />
        );

      case ATTENDEE_STATUS.ANSWERED_INCORRECT:
        return (
          <Box
            w={4}
            h={4}
            bg="red.500"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            data-testid="attendee-status-incorrect"
          >
            <CloseIcon w={2} h={2} color="white" />
          </Box>
        );

      case ATTENDEE_STATUS.WAITING:
        return (
          <Box
            px={2}
            py={1}
            bg="#FEF0C7"
            borderRadius="6px"
            boxShadow="0px 0.43883px 0.87766px rgba(16, 24, 40, 0.06), 0px 0.43883px 1.31649px rgba(16, 24, 40, 0.1)"
            data-testid="attendee-status-waiting"
          >
            <Text fontSize="10px" fontWeight="400" color="#7A2E0E">
              Waiting
            </Text>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Flex
      justify="space-between"
      align="center"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="9px"
      bg="white"
      gap={20}
      p={2}
      data-testid={`attendee-card-${attendee.user.userId}`}
    >
      <Flex align="center" gap={1.5} flex={1}>
        <Avatar
          size="sm"
          name={`${attendee.user.firstName} ${attendee.user.lastName}`}
          src={attendee.user.avatar}
          w={8}
          h={8}
        />

        <Flex direction="column" justify="center" gap={0.5} flex={1}>
          <Flex align="center" gap={1.5} justify="space-between">
            <Text
              fontSize="12px"
              fontWeight="500"
              color="#2B355C"
              lineHeight="18px"
            >
              {attendee.user.firstName} {attendee.user.lastName}
            </Text>
            <Box data-testid="attendee-status">
              <StatusIndicator />
            </Box>
          </Flex>

          <Flex align="center" gap={1.5}>
            <Flex align="center" gap={0.5}>
              <Box
                w="11px"
                h="11px"
                bg="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <svg
                  width="11"
                  height="10"
                  viewBox="0 0 11 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.50033 0.41626C2.97314 0.41626 0.916992 2.47241 0.916992 4.99959C0.916992 5.75019 1.1147 6.45178 1.4353 7.07731L0.938477 8.85604C0.822758 9.26932 1.23139 9.67793 1.64478 9.56234L3.42485 9.06551C4.04987 9.38545 4.75043 9.58293 5.50033 9.58293C8.02751 9.58293 10.0837 7.52678 10.0837 4.99959C10.0837 2.47241 8.02751 0.41626 5.50033 0.41626ZM5.50033 2.47921C6.13214 2.47921 6.64616 2.99323 6.64616 3.62504C6.64616 4.25685 6.13214 4.77087 5.50033 4.77087C4.86851 4.77087 4.35449 4.25685 4.35449 3.62504C4.35449 2.99323 4.86851 2.47921 5.50033 2.47921ZM4.01074 5.68754H6.98991C7.3057 5.68754 7.56283 5.94444 7.56283 6.26046V6.48962C7.56283 6.59183 7.55106 6.69238 7.52791 6.78817C7.51302 6.85027 7.48108 6.90707 7.43571 6.95199C6.9155 7.46669 6.22816 7.75004 5.50033 7.75004C4.77249 7.75004 4.08538 7.46669 3.56494 6.95199C3.51957 6.90707 3.48719 6.85027 3.47229 6.78817C3.44937 6.69238 3.43783 6.59183 3.43783 6.48962V6.26046C3.43783 5.94444 3.69495 5.68754 4.01074 5.68754Z"
                    fill="#AAAEBE"
                  />
                </svg>
              </Box>
              <Text
                fontSize="8px"
                fontWeight="500"
                color="#80859D"
                lineHeight="9.77px"
                data-testid="attendee-timestamp"
              >
                {formatTimestamp(responseDetails.timestamp)}
              </Text>
            </Flex>

            <Flex align="center" gap={1}>
              <Text
                fontSize="8px"
                fontWeight="500"
                color="#80859D"
                lineHeight="9.77px"
                data-testid="attendee-response-text"
              >
                {responseDetails.text}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default AttendeeCard;
